// ─────────────────────────────────────────────────────────────────────────────
// realtimeService.js — Phase 2 (P2-S4)
// Supabase Broadcast channel wrapper — singleton per browser tab.
//
// Both Tab7_Exercise (host) and Tab7_TrainerPanel (child) share the same
// instance via module-level state. No auth required for Broadcast channels.
//
// Channel name convention: `exercise:{SESSION_CODE}` (upper-case)
//
// Events:
//   inject_fired   { inject_type, op_day, label }
//   clock_update   { started_at, running, compression_min }
//   room_closed    {}
//   custom_inject  { id, title, narrative, options[], op_day, fired_at }   ← P3-IMP-001
//
// Usage:
//   realtimeService.connect('ROMEO-7');          // connect (trainer or student)
//   realtimeService.send('inject_fired', {...}); // publish (trainer only)
//   const off = realtimeService.on('inject_fired', handler); // subscribe
//   off(); // unsubscribe one handler
//   realtimeService.disconnect();                // cleanup
// ─────────────────────────────────────────────────────────────────────────────

import { supabase } from './supabaseClient';

// Module-level state — one channel per browser tab at a time
let _channel  = null;
const _handlers = {}; // { event: [handler, ...] }

function _dispatch(event, payload) {
  (_handlers[event] || []).forEach(h => {
    try { h(payload); } catch (_) {}
  });
}

export const realtimeService = {

  // ── Connect to the exercise broadcast channel ─────────────────────────────
  // Safe to call repeatedly — disconnects previous channel first.
  connect(sessionCode) {
    if (_channel) this.disconnect();

    _channel = supabase
      .channel(`exercise:${sessionCode.toUpperCase()}`, {
        config: { broadcast: { self: false } }, // don't echo back to sender
      })
      .on('broadcast', { event: 'inject_fired'     }, ({ payload }) => _dispatch('inject_fired',     payload))
      .on('broadcast', { event: 'clock_update'    }, ({ payload }) => _dispatch('clock_update',    payload))
      .on('broadcast', { event: 'room_closed'     }, ({ payload }) => _dispatch('room_closed',     payload))
      .on('broadcast', { event: 'student_decision'}, ({ payload }) => _dispatch('student_decision', payload)) // P2-S5
      .on('broadcast', { event: 'custom_inject'   }, ({ payload }) => _dispatch('custom_inject',   payload)) // P3-IMP-001
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // eslint-disable-next-line no-console
          console.info(`[PSP/CRE] Realtime connected: exercise:${sessionCode.toUpperCase()}`);
        }
      });

    return this; // chainable
  },

  // ── Register a handler for an event ──────────────────────────────────────
  // Returns an unsubscribe function — call it in useEffect cleanup.
  on(event, handler) {
    if (!_handlers[event]) _handlers[event] = [];
    _handlers[event].push(handler);
    return () => {
      _handlers[event] = (_handlers[event] || []).filter(h => h !== handler);
    };
  },

  // ── Broadcast an event to all channel subscribers ─────────────────────────
  // No-op if not connected (safe to call anytime).
  send(event, payload = {}) {
    if (!_channel) return;
    _channel.send({ type: 'broadcast', event, payload });
  },

  // ── Disconnect and clean up all handlers ──────────────────────────────────
  disconnect() {
    if (_channel) {
      supabase.removeChannel(_channel);
      _channel = null;
    }
    Object.keys(_handlers).forEach(k => { _handlers[k] = []; });
  },
};
